// @ts-nocheck - Amplify Gen 2 type compatibility issues
/**
 * Constants for Energy Tariff Calculator
 *
 * @description Pricing data, fees, and configuration constants
 * @version 2.1.0
 * @created 2025-01-26
 * @author eCompanion2 Energy Tariff Calculator
 *
 * Extracted from tariff-calculator.ts for better modularity
 */

import type { TauronPricing, PSEZone, ConsumptionPattern } from './types';

// Import pricing from JSON configs via pricing-config module
import {
  VAT_RATE as _VAT_RATE,
  HANDLING_FEE as _HANDLING_FEE,
  OZE_FEE as _OZE_FEE,
  OZE_FEE_2026 as _OZE_FEE_2026,
  COGENERATION_FEE as _COGENERATION_FEE,
  COGENERATION_FEE_2026 as _COGENERATION_FEE_2026,
  SUBSCRIPTION_FEE as _SUBSCRIPTION_FEE,
  TRANSITION_FEE as _TRANSITION_FEE,
  POWER_FEE as _POWER_FEE,
  POWER_FEE_2026 as _POWER_FEE_2026,
  getOzeFeeForYear as _getOzeFeeForYear,
  getCogenerationFeeForYear as _getCogenerationFeeForYear,
  getPowerFeeForConsumption as _getPowerFeeForConsumption,
  getPowerFeeTierForYear as _getPowerFeeTierForYear,
  isPowerFeeFrozen as _isPowerFeeFrozen,
  TAURON_2025_PRICING as _TAURON_2025_PRICING,
  TAURON_2026_PRICING as _TAURON_2026_PRICING,
  getPricingForYear as _getPricingForYear,
} from '../pricing-config';

// ============================================================================
// FEES AND VAT
// ============================================================================

/**
 * VAT Rate (23%)
 */
export const VAT_RATE = _VAT_RATE;

/**
 * Handling Fee (Opłata handlowa)
 */
export const HANDLING_FEE = _HANDLING_FEE;

/**
 * OZE Fee (Opłata OZE) - 2025
 */
export const OZE_FEE = _OZE_FEE;

/**
 * OZE Fee (Opłata OZE) - 2026
 */
export const OZE_FEE_2026 = _OZE_FEE_2026;

/**
 * Cogeneration Fee (Opłata kogeneracyjna) - 2025
 */
export const COGENERATION_FEE = _COGENERATION_FEE;

/**
 * Cogeneration Fee (Opłata kogeneracyjna) - 2026
 */
export const COGENERATION_FEE_2026 = _COGENERATION_FEE_2026;

/**
 * Subscription Fee (Opłata abonamentowa)
 */
export const SUBSCRIPTION_FEE = _SUBSCRIPTION_FEE;

/**
 * Transition Fee (Opłata przejściowa)
 */
export const TRANSITION_FEE = _TRANSITION_FEE;

/**
 * Power Fee (Opłata mocowa) - 2025
 */
export const POWER_FEE = _POWER_FEE;

/**
 * Power Fee (Opłata mocowa) - 2026
 */
export const POWER_FEE_2026 = _POWER_FEE_2026;

// ============================================================================
// FEE HELPER FUNCTIONS
// ============================================================================

/**
 * Get OZE fee for a given year
 */
export const getOzeFeeForYear = _getOzeFeeForYear;

/**
 * Get Cogeneration fee for a given year
 */
export const getCogenerationFeeForYear = _getCogenerationFeeForYear;

/**
 * Get Power fee for consumption tier
 */
export const getPowerFeeForConsumption = _getPowerFeeForConsumption;

/**
 * Get Power fee tier for year
 */
export const getPowerFeeTierForYear = _getPowerFeeTierForYear;

/**
 * Check if power fee is frozen for given month and year
 */
export const isPowerFeeFrozen = _isPowerFeeFrozen;

// ============================================================================
// CALENDAR CONSTANTS
// ============================================================================

/**
 * Days in Month Lookup
 */
export const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Leap Year Days in Month
 */
export const DAYS_IN_MONTH_LEAP = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// ============================================================================
// TIME ZONE CONSTANTS
// ============================================================================

/**
 * G12 Zone Hours (TAURON Dystrybucja)
 *
 * Expensive zone (Strefa I): 06:00-13:00 + 15:00-22:00 (14 hours)
 * Cheap zone (Strefa II): 13:00-15:00 + 22:00-06:00 (10 hours)
 *
 * Source: TAURON official tariff, confirmed by URE
 */
export const G12_DAY_START_HOUR = 6;
export const G12_DAY_END_HOUR = 22;
export const G12_OFFPEAK_MIDDAY_START = 13;
export const G12_OFFPEAK_MIDDAY_END = 15;

/**
 * Check if an hour falls in the G12 expensive (day) zone
 *
 * Expensive: 06:00-13:00 + 15:00-22:00
 * Cheap: 13:00-15:00 + 22:00-06:00
 *
 * @param hour - Hour of day (0-23)
 * @returns true if hour is in expensive (day) zone
 */
export function isG12DayHour(hour: number): boolean {
  return (hour >= G12_DAY_START_HOUR && hour < G12_OFFPEAK_MIDDAY_START) ||
         (hour >= G12_OFFPEAK_MIDDAY_END && hour < G12_DAY_END_HOUR);
}

/**
 * OSD-specific G12 zone configuration
 * Each OSD has different midday off-peak windows
 */
export type OSDZoneConfig = {
  dayStart: number;
  dayEnd: number;
  middayOffPeak?:
    | { start: number; end: number }
    | { winter: { start: number; end: number }; summer: { start: number; end: number } };
};

/**
 * G12 zone definitions per OSD operator
 * - TAURON: fixed midday off-peak 13:00-15:00 year-round
 * - PGE: seasonal midday off-peak (winter 13-15, summer 15-17)
 * - ENEA: fixed midday off-peak 13:00-15:00 year-round
 * - ENERGA: fixed midday off-peak 13:00-15:00 year-round
 */
export const G12_ZONE_BY_OSD: Record<string, OSDZoneConfig> = {
  TAURON: { dayStart: 6, dayEnd: 22, middayOffPeak: { start: 13, end: 15 } },
  PGE: { dayStart: 6, dayEnd: 22, middayOffPeak: { winter: { start: 13, end: 15 }, summer: { start: 15, end: 17 } } },
  ENEA: { dayStart: 6, dayEnd: 22, middayOffPeak: { start: 13, end: 15 } },
  ENERGA: { dayStart: 6, dayEnd: 22, middayOffPeak: { start: 13, end: 15 } },
};

/**
 * Check if an hour falls in the G12 expensive (day) zone for a specific OSD
 *
 * @param hour - Hour of day (0-23)
 * @param osd - OSD operator name (TAURON, PGE, ENEA, ENERGA)
 * @param month - Month (0-indexed, 0=Jan) - needed for PGE seasonal zones
 * @returns true if hour is in expensive (day) zone
 */
export function isG12DayHourForOSD(hour: number, osd: string, month?: number): boolean {
  const config = G12_ZONE_BY_OSD[osd.toUpperCase()] || G12_ZONE_BY_OSD.TAURON;

  // Outside day range entirely = night
  if (hour < config.dayStart || hour >= config.dayEnd) return false;

  // No midday off-peak = straight day zone
  if (!config.middayOffPeak) return true;

  // Fixed midday off-peak (TAURON)
  if ('start' in config.middayOffPeak) {
    return hour < config.middayOffPeak.start || hour >= config.middayOffPeak.end;
  }

  // Seasonal midday off-peak (PGE)
  const isWinter = month !== undefined && (month >= 9 || month <= 2);
  const seasonal = isWinter ? config.middayOffPeak.winter : config.middayOffPeak.summer;
  return hour < seasonal.start || hour >= seasonal.end;
}

// ============================================================================
// CONSUMPTION PATTERNS
// ============================================================================

/**
 * Default Uniform Pattern (all hours = 1.0)
 */
export const UNIFORM_PATTERN: ConsumptionPattern = {
  weekday: Array(24).fill(1.0),
  weekend: Array(24).fill(1.0)
};

// ============================================================================
// PSE ZONE DISTRIBUTION RATES
// ============================================================================

/**
 * PSE Zone Distribution Rates (net PLN/kWh)
 * Source: Tauron Dystrybucja G14 Tariff 2025
 */
export const ZONE_DISTRIBUTION_RATES: Record<PSEZone, number> = {
  GREEN: 0.0118,        // Lowest rate - optimal usage time
  LIGHT_GREEN: 0.0479,  // Good rate
  YELLOW: 0.3521,       // Expensive - minimize usage
  RED: 2.3521,          // Very expensive - avoid usage
};

// ============================================================================
// PRICING DATA
// ============================================================================

/**
 * Tauron 2025 Pricing Data (loaded from JSON config)
 * Source: amplify/data/config/pricing-2025.json
 */
export const TAURON_2025_PRICING: TauronPricing = _TAURON_2025_PRICING as TauronPricing;

/**
 * Tauron 2026 Pricing Data (loaded from JSON config)
 * Source: amplify/data/config/pricing-2026.json
 */
export const TAURON_2026_PRICING: TauronPricing = _TAURON_2026_PRICING as TauronPricing;

/**
 * Get pricing data for a given year
 * @param year - Year (2025 or 2026)
 * @returns Tauron pricing data for that year
 */
export function getPricingForYear(year: number): TauronPricing {
  return _getPricingForYear(year) as TauronPricing;
}
